// Button (DESIGN §3.6.1): Pill, Varianten primary/secondary/ghost/danger/danger-ghost/link,
// Größen sm 32 · md 40 · lg 48, Icon links/rechts, loading mit Spinner bei gleicher Breite.
import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import { Slot } from 'radix-ui';
import { cn, cva, Spinner, type VariantProps } from '../basis/kern';

/** Fläche, auf der der Knopf liegt; bestimmt die Pill-Farbe von secondary/ghost (DESIGN §2.3). */
export type Surface = 'page' | 'card' | 'stripe' | 'tile';

export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-2 rounded-full whitespace-nowrap select-none',
    'transition-[background-color,color,transform] duration-(--bar-dur-fast) ease-standard',
    'active:scale-[.97] disabled:cursor-not-allowed disabled:text-fg-disabled disabled:active:scale-100',
    'aria-disabled:cursor-not-allowed aria-disabled:text-fg-disabled aria-disabled:active:scale-100',
    'aria-busy:cursor-progress',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-primary text-on-primary hover:not-disabled:bg-primary-hover',
        secondary: 'text-fg',
        ghost: 'bg-transparent text-fg',
        danger: 'bg-danger-solid text-on-danger-solid hover:not-disabled:brightness-110',
        'danger-ghost': 'bg-transparent text-danger-fg hover:not-disabled:bg-danger-soft',
        link: 'h-auto! rounded-sm bg-transparent px-0! text-accent-fg hover:underline active:scale-100',
      },
      surface: { page: '', card: '', stripe: '', tile: '' },
      size: {
        sm: 'h-8 px-3 text-label font-medium',
        md: 'h-10 px-4 text-button',
        lg: 'h-12 px-5 text-button',
      },
      block: { true: 'w-full', false: '' },
    },
    compoundVariants: [
      {
        variant: 'secondary',
        surface: 'page',
        class: 'bg-pill-page hover:not-disabled:bg-pill-page-hover',
      },
      {
        variant: 'secondary',
        surface: 'card',
        class: 'bg-pill-card hover:not-disabled:bg-pill-card-hover',
      },
      {
        variant: 'secondary',
        surface: 'stripe',
        class: 'bg-pill-stripe hover:not-disabled:bg-pill-stripe-hover',
      },
      {
        variant: 'secondary',
        surface: 'tile',
        class: 'bg-pill-tile hover:not-disabled:bg-pill-tile-hover',
      },
      { variant: 'ghost', surface: 'page', class: 'hover:not-disabled:bg-pill-page' },
      { variant: 'ghost', surface: 'card', class: 'hover:not-disabled:bg-pill-card' },
      { variant: 'ghost', surface: 'stripe', class: 'hover:not-disabled:bg-pill-stripe' },
      { variant: 'ghost', surface: 'tile', class: 'hover:not-disabled:bg-pill-tile' },
    ],
    defaultVariants: { variant: 'secondary', surface: 'page', size: 'md', block: false },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: Ref<HTMLButtonElement>;
  variant?: ButtonVariant;
  surface?: Surface;
  size?: ButtonSize;
  block?: boolean;
  /** Icon links (Phosphor, 16). Wird bei `loading` zum Spinner. */
  icon?: ReactNode;
  /** Icon rechts, z. B. `ArrowSquareOutIcon` 14 bei Plattformwechsel. */
  iconRight?: ReactNode;
  loading?: boolean;
  /** Rendert das einzige Kind (z. B. Router-Link) mit Knopf-Optik. */
  asChild?: boolean;
}

export function Button({
  variant = 'secondary',
  surface = 'page',
  size = 'md',
  block = false,
  icon,
  iconRight,
  loading = false,
  asChild = false,
  type = 'button',
  className,
  children,
  onClick,
  ...rest
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';
  const spinnerSize = size === 'sm' ? 14 : 16;
  const leading = loading ? <Spinner size={spinnerSize} /> : icon;
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading || rest['aria-disabled'] === true || rest['aria-disabled'] === 'true') {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  return (
    <Comp
      {...(asChild ? {} : { type })}
      {...rest}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-size={size}
      data-state={loading ? 'loading' : undefined}
      onClick={handleClick}
      className={cn(buttonVariants({ variant, surface, size, block }), className)}
    >
      {leading ? (
        <span aria-hidden="true" className="inline-flex shrink-0">
          {leading}
        </span>
      ) : null}
      {asChild ? <Slot.Slottable>{children}</Slot.Slottable> : children}
      {iconRight ? (
        <span aria-hidden="true" className="inline-flex shrink-0">
          {iconRight}
        </span>
      ) : null}
    </Comp>
  );
}
